/**
 * Etapa 2 — O que é armazenado (#366, §3.2): os produtos da instalação, a classe que o servidor
 * calcula a partir deles e o formulário de produto. Sem `canEdit`, a etapa só mostra.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, Typography } from '@mui/material';
import { ConfirmDialog } from '../../../../../components/common/ConfirmDialog';
import { DataTable, type DataTableColumn } from '../../../../../components/common/DataTable';
import { flammableInstallationService } from '../../../../../services/flammableInstallationService';
import {
  ROTULO_DA_CATEGORIA_DO_PRODUTO,
  ROTULO_DO_ESTADO_FISICO,
  type FlammableInstallationSummary,
  type FlammableSubstance,
} from '../../../../../types/flammableInstallation';
import { mensagemDoServidor } from '../../mensagensDeErro';
import type { ListStepProps, ResultadoDoEnvio, StepHandle } from '../FlammableInstallationWizard.types';
import { ClasseCalculada } from './ClasseCalculada';
import { FormularioDoProduto } from './FormularioDoProduto';
import {
  PRODUTO_VAZIO,
  UNIDADE_CURTA_DO_ESTADO,
  produtoParaEnvio,
  validarProduto,
  valoresDoProduto,
  type ProdutoErrors,
  type ProdutoValues,
} from './produtoDoFormulario';

const numeroEmTexto = (valor: number): string => valor.toLocaleString('pt-BR', { maximumFractionDigits: 3 });

/** Formulário aberto: `null` fechado, `novo` ou o produto em edição. */
type Aberto = { produto: FlammableSubstance | null } | null;

export const StepProdutos = forwardRef<StepHandle, ListStepProps>(function StepProdutos(
  { installationId, canEdit, onDirtyChange, onSaved, onClassificationChange },
  ref
) {
  const [produtos, setProdutos] = useState<FlammableSubstance[]>([]);
  const [summary, setSummary] = useState<FlammableInstallationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [aberto, setAberto] = useState<Aberto>(null);
  const [values, setValues] = useState<ProdutoValues>(PRODUTO_VAZIO);
  const [errors, setErrors] = useState<ProdutoErrors>({});
  const [erroDoServidor, setErroDoServidor] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [paraExcluir, setParaExcluir] = useState<FlammableSubstance | null>(null);
  const [excluindo, setExcluindo] = useState(false);
  const [erroAoExcluir, setErroAoExcluir] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  const valoresIniciais = aberto?.produto ? valoresDoProduto(aberto.produto) : PRODUTO_VAZIO;
  const dirty = aberto !== null && JSON.stringify(values) !== JSON.stringify(valoresIniciais);

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  /** Lê os produtos e o resumo do servidor; a classe nunca é calculada aqui. */
  const carregar = useCallback(async () => {
    const [encontrados, instalacao] = await Promise.all([
      flammableInstallationService.getSubstances(installationId),
      flammableInstallationService.getInstallationById(installationId),
    ]);
    setProdutos(encontrados);
    setSummary(instalacao.summary ?? null);
    if (instalacao.summary) onClassificationChange?.(instalacao.summary.classification);
  }, [installationId, onClassificationChange]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    carregar()
      .catch((err: unknown) => {
        if (active) setLoadError(mensagemDoServidor(err, 'Não foi possível carregar os produtos.'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [carregar]);

  const tentarDeNovo = () => {
    setLoadError(null);
    setLoading(true);
    carregar()
      .catch((err: unknown) => setLoadError(mensagemDoServidor(err, 'Não foi possível carregar os produtos.')))
      .finally(() => setLoading(false));
  };

  const abrir = useCallback((produto: FlammableSubstance | null) => {
    setValues(produto ? valoresDoProduto(produto) : PRODUTO_VAZIO);
    setErrors({});
    setErroDoServidor(null);
    setAberto({ produto });
    // O formulário abre embaixo da lista; quem clicou em "Editar" lá em cima precisa vê-lo.
    requestAnimationFrame(() => formRef.current?.scrollIntoView?.({ block: 'nearest' }));
  }, []);

  const fechar = () => {
    setAberto(null);
    setValues(PRODUTO_VAZIO);
    setErrors({});
    setErroDoServidor(null);
  };

  const alterar = (patch: Partial<ProdutoValues>) => {
    setValues((anterior) => ({ ...anterior, ...patch }));
    setErrors((anterior) => {
      const proximo = { ...anterior };
      (Object.keys(patch) as Array<keyof ProdutoValues>).forEach((chave) => delete proximo[chave]);
      return proximo;
    });
    setErroDoServidor(null);
  };

  const enviar = useCallback(async (): Promise<ResultadoDoEnvio> => {
    if (aberto === null) return 'enviado';
    const encontrados = validarProduto(values);
    setErrors(encontrados);
    if (Object.keys(encontrados).length > 0) {
      setErroDoServidor('Corrija os campos marcados para guardar o produto.');
      return 'incompleto';
    }
    setSaving(true);
    setErroDoServidor(null);
    try {
      const corpo = produtoParaEnvio(values);
      if (aberto.produto) {
        await flammableInstallationService.updateSubstance(installationId, String(aberto.produto.id), corpo);
      } else {
        await flammableInstallationService.createSubstance(installationId, corpo);
      }
      setAberto(null);
      setValues(PRODUTO_VAZIO);
      onSaved('Produto salvo.');
      try {
        await carregar();
      } catch {
        setLoadError('Salvou, mas não consegui atualizar a lista de produtos.');
      }
      return 'enviado';
    } catch (err: unknown) {
      setErroDoServidor(mensagemDoServidor(err, 'Não foi possível guardar o produto.'));
      return 'recusado';
    } finally {
      setSaving(false);
    }
  }, [aberto, carregar, installationId, onSaved, values]);

  useImperativeHandle(
    ref,
    () => ({
      salvarPendente: async () => (dirty ? enviar() : 'enviado'),
    }),
    [dirty, enviar]
  );

  const confirmarExclusao = async () => {
    if (!paraExcluir) return;
    setExcluindo(true);
    setErroAoExcluir(null);
    try {
      await flammableInstallationService.deleteSubstance(installationId, String(paraExcluir.id));
      if (aberto?.produto?.id === paraExcluir.id) fechar();
      setParaExcluir(null);
      onSaved('Produto excluído.');
      await carregar();
    } catch (err: unknown) {
      setErroAoExcluir(mensagemDoServidor(err, 'Não foi possível excluir o produto.'));
    } finally {
      setExcluindo(false);
    }
  };

  const columns = useMemo<DataTableColumn<FlammableSubstance>[]>(() => {
    const base: DataTableColumn<FlammableSubstance>[] = [
      {
        id: 'produto',
        label: 'Produto',
        primary: true,
        render: (produto) => (
          <Box>
            <Typography variant="body1" fontWeight={600}>
              {produto.productName}
            </Typography>
            {produto.storageLocation && (
              <Typography variant="body2" color="text.secondary">
                {produto.storageLocation}
              </Typography>
            )}
          </Box>
        ),
      },
      { id: 'estado', label: 'Estado', render: (produto) => ROTULO_DO_ESTADO_FISICO[produto.physicalState] },
      {
        id: 'fulgor',
        label: 'Ponto de fulgor',
        align: 'right',
        render: (produto) =>
          produto.flashPointCelsius === null ? '—' : `${numeroEmTexto(produto.flashPointCelsius)} °C`,
      },
      {
        id: 'categoria',
        label: 'Categoria',
        render: (produto) =>
          produto.category ? (
            <Chip size="small" variant="outlined" label={ROTULO_DA_CATEGORIA_DO_PRODUTO[produto.category]} />
          ) : (
            '—'
          ),
      },
      {
        id: 'capacidade',
        label: 'Capacidade',
        align: 'right',
        render: (produto) => `${numeroEmTexto(produto.capacity)} ${UNIDADE_CURTA_DO_ESTADO[produto.physicalState]}`,
      },
      {
        id: 'ficha',
        label: 'Ficha de segurança',
        render: (produto) => (
          <Chip
            size="small"
            variant="outlined"
            color={produto.sdsAvailable ? 'success' : 'default'}
            label={produto.sdsAvailable ? 'Disponível' : 'Não tem'}
          />
        ),
      },
    ];
    if (!canEdit) return base;
    return [
      ...base,
      {
        id: 'acoes',
        label: 'Ações',
        align: 'center',
        render: (produto) => (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
            <Button
              variant="text"
              aria-label={`Editar ${produto.productName}`}
              disabled={dirty && aberto?.produto?.id !== produto.id}
              onClick={() => abrir(produto)}
              sx={{ minHeight: 44 }}
            >
              Editar
            </Button>
            <Button
              variant="text"
              color="error"
              aria-label={`Excluir ${produto.productName}`}
              onClick={() => {
                setErroAoExcluir(null);
                setParaExcluir(produto);
              }}
              sx={{ minHeight: 44 }}
            >
              Excluir
            </Button>
          </Box>
        ),
      },
    ];
  }, [abrir, aberto, canEdit, dirty]);

  return (
    <Box>
      <Box sx={{ maxWidth: 720, mb: 3 }}>
        <Typography variant="h6" component="h3">
          O que esta instalação armazena
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {canEdit
            ? 'Cadastre cada produto inflamável ou combustível. O sistema soma as capacidades e calcula a classe da instalação.'
            : 'Aqui estão os produtos que esta instalação armazena e a classe que o sistema calculou a partir deles.'}
        </Typography>
      </Box>

      {loadError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" onClick={tentarDeNovo} sx={{ minHeight: 44 }}>
              Tentar de novo
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}
      {!loadError && summary && <ClasseCalculada summary={summary} />}

      {!loadError && (
        <DataTable
          embedded
          columns={columns}
          rows={produtos}
          getRowId={(produto) => String(produto.id)}
          loading={loading}
          emptyState={
            canEdit
              ? {
                  title: 'Nenhum produto cadastrado nesta instalação',
                  description:
                    'A classe da instalação depende do que ela armazena: gás em toneladas, líquido em m³. Cadastre o primeiro produto abaixo para o sistema calcular a classe e dizer quais documentos a norma pede.',
                }
              : {
                  title: 'Nenhum produto cadastrado nesta instalação',
                  description: 'A classe da instalação depende do que ela armazena. Nenhum produto foi cadastrado ainda.',
                }
          }
        />
      )}

      <Box ref={formRef}>
        {canEdit && !loadError && aberto === null && (
          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={() => abrir(null)} sx={{ minHeight: 44 }}>
              Cadastrar produto
            </Button>
          </Box>
        )}
        {canEdit && aberto !== null && (
          <FormularioDoProduto
            titulo={aberto.produto ? `Editar: ${aberto.produto.productName}` : 'Novo produto'}
            values={values}
            errors={errors}
            erroDoServidor={erroDoServidor}
            editando={aberto.produto !== null}
            salvando={saving}
            onChange={alterar}
            onSubmit={() => void enviar()}
            onCancel={fechar}
          />
        )}
      </Box>

      <ConfirmDialog
        open={paraExcluir !== null}
        title="Excluir este produto?"
        message={
          paraExcluir
            ? `"${paraExcluir.productName}" sai da instalação, a capacidade somada muda e a classe é calculada de novo. Não dá para desfazer.`
            : ''
        }
        confirmLabel="Excluir produto"
        loading={excluindo}
        error={erroAoExcluir}
        onConfirm={() => void confirmarExclusao()}
        onCancel={() => setParaExcluir(null)}
      />
    </Box>
  );
});

export default StepProdutos;
